import { Injectable } from '@angular/core';
import { MarkerClusterService } from './marker-cluster.service';
import { IGoogleMap, ILatLng, MarkerIcon } from 'src/app/core/models/google-map-models';
import { ITrafficAlertCarkPark, IBoundingBoxModel, ITravelTopHits } from 'src/app/core/models/models';
import { AAGoogleMarkerService } from 'src/app/modules/aa-core/services/google/google-marker/aa-google-marker.service';
import { AAGoogleIconService } from 'src/app/modules/aa-core/services/google/aa-google-icon/aagoogle-icon.service';
import { AppService } from 'src/app/modules/aa-core/services/app-service/app.service';
import { GoogleInfoWindowService } from '../google-info-window/google-info-window.service';
import { TRAVEL_TYPE } from 'src/app/modules/maps-hotels/models/maps-hotels-model';
import { GoogleMapHTTPService } from 'src/app/modules/aa-core/services/google/google-map-http/google-map-http.service';
import { AAClusterType } from '../../enums/google-map-enum';
import { MapContextMenuEvents } from 'src/app/core/events/events';
import { EnvironmentService } from 'src/app/environment.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TravelMarkerService extends MarkerClusterService {
  private travelMarkers: Array<any> = [];
  private travelData: ITrafficAlertCarkPark[];
  private bookingUrl: any = '';

  constructor(
    private aaMarkerService: AAGoogleMarkerService,
    private appService: AppService,
    private envService: EnvironmentService,
    private environmentService: EnvironmentService,
    private googleHttp: GoogleMapHTTPService,
    private googleInfoWindow: GoogleInfoWindowService,
    private googleIconManager: AAGoogleIconService
  ) {
    super();
  }

  public loadTravelData(googleMap: IGoogleMap, travelTypes: Array<TRAVEL_TYPE>): void {
    this.loadTravelRequest(googleMap, travelTypes).subscribe((data) => {
      this.travelData = data;
      this.addTravelMarker(googleMap);
    });
  }

  public loadTravelRequest(googleMap: IGoogleMap, travelTypes: Array<TRAVEL_TYPE>): Observable<any> {
    const boundingBox: IBoundingBoxModel = this.getBoundinBoxData(googleMap);
    return this.googleHttp.getTravel(boundingBox, travelTypes);
  }

  public clear(): void {
    this.travelMarkers.forEach((marker) => {
      marker.setMap(null);
    });
    this.travelMarkers = [];
  }

  private addTravelMarker = (googleMap: IGoogleMap) => {
    this.clear();
    for (let markerCounter = 0; markerCounter < this.travelData.length; markerCounter++) {
      const carParkModel: ITrafficAlertCarkPark = this.travelData[markerCounter];

      const dummyMarkerCount: number = carParkModel.hitCount - carParkModel.topHits.length;
      if (dummyMarkerCount > 0) {
        const marker = this.aaMarkerService.createClusterMarker(
          googleMap,
          carParkModel,
          carParkModel.hitCount,
          AAClusterType.TRAVEL_CLUSTER
        );
        this.travelMarkers.push(marker);
      } else {
        for (let tophitCounter = 0; tophitCounter < carParkModel.topHits.length; tophitCounter++) {
          const topHitModel: ITravelTopHits = carParkModel.topHits[tophitCounter] as ITravelTopHits;
          this.createTravelMarker(topHitModel, googleMap);
        }
      }
    }
  };

  private createTravelMarker = (travelData: ITravelTopHits, googleMap: IGoogleMap) => {
    const icon: MarkerIcon = this.getIcon(travelData.categoryId);

    const cpShape = {
      coord: [1, 1, 1, 30, 28, 30, 28, 1],
      type: 'poly'
    };

    const latLng: ILatLng = new google.maps.LatLng(travelData.lat, travelData.lon);
    const cpMarker = new google.maps.Marker({
      position: latLng,
      map: googleMap,
      icon: icon,
      title: travelData.name || 'Travel Park',
      shape: cpShape,
      // shadow: IconShadow,
      html: this.getMarkerInfoWindowHtmlContent(travelData)
    });

    cpMarker.category = 'carpark';
    cpMarker.data = travelData;
    // cpMarker.setVisible(this.googleMapOptions.CarPark);

    google.maps.event.addListener(cpMarker, 'click', () => {
      this.googleInfoWindow.close();
      this.googleInfoWindow.setContent(cpMarker.html);
      this.googleInfoWindow.open(googleMap, cpMarker);
    });
    this.travelMarkers.push(cpMarker);

    return cpMarker;
  };

  private getIcon(categoryId: TRAVEL_TYPE): MarkerIcon {
    switch (categoryId) {
      case TRAVEL_TYPE.HOT:
      case TRAVEL_TYPE.GAC:
      case TRAVEL_TYPE.BUD:
        return this.googleIconManager.getHotelIcon();

      case TRAVEL_TYPE.RES:
        return this.googleIconManager.getRestaurantIcon();
    }
  }

  private getImages(markerDataObj): string {
    let filename = '';

    if (markerDataObj.hasOwnProperty('imageList') && markerDataObj.imageList && markerDataObj.imageList.length) {
      filename = markerDataObj.imageList[0];
    } else {
      filename = markerDataObj.categoryId === TRAVEL_TYPE.RES ? `generic-restaurant-thumbnail.jpg` : `generic-hotel-thumbnail.jpg`;
    }

    if (this.environmentService.isProduction) {
      return `/route-planner/travel-images/establishments/${filename}`;
    } else {
      return `https://sites.dev.theaa.digital/route-planner/travel-images/establishments/${filename}`;
    }
  }

  private getMarkerInfoWindowHtmlContent = (markerDataObj: any) => {
    // console.log('Popup Data:', markerDataObj);
    const navigateDirection = `javascript:${MapContextMenuEvents.UPDATE_TO_LOCATION}('', ${markerDataObj.lat}, ${markerDataObj.lon})`;
    const directionLink = `<a class="direction-link" href="${navigateDirection}">Directions</a>`;
    const $title: string = markerDataObj.category;
    const detailLink = markerDataObj.bookingURL + '?utm_source=aarouteplanner&utm_medium=website&utm_campaign=' + markerDataObj.categoryId  ;
    const ratingType = markerDataObj.rating;
    let ratingValue = '';
    if (['Awaiting assessment', 'Awaiting inspection', 'Rosettes suspended', 'Rating suspended'].includes(ratingType)) {
      ratingValue = ratingType;
    } else if (markerDataObj.categoryId !== TRAVEL_TYPE.RES && ratingType <= 2) {
      ratingValue = 'Quality Assessed';
    } else {
      for (let i = 0; i < ratingType; i++) {
        ratingValue +=
          markerDataObj.categoryId == TRAVEL_TYPE.RES ? `<span class="ratingRosette"></span>` : `<span class="ratingStar"></span>`;
      }
    }
    const travelImages = this.getImages(markerDataObj);

    if (markerDataObj.bookingURL == '') {
      this.bookingUrl = 'disabled';
    } else {
      this.bookingUrl = '';
    }

    // Info Map window html content
    return `<div class='iw-hotels' id='hotel-window'>
        <h3>${$title}</h3>
        <div class="hotel-wrapper"><img src="${travelImages}"/></div>
        <div class="hotel-name">${markerDataObj.name || ''}</div>
        <div class="hotel-rating">${ratingValue}</div>
        <div class="iw-wrapper">
          <div class="iw-content">${directionLink}</div>
          <div>
          <a href="${detailLink}" target="_blank" class="btn primary detail-btn ${this.bookingUrl}" role="button">Details</a>
          </div>

        </div>

      </div>`;
  };
}
